import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  ShareableObj,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { Observable, Subject } from 'rxjs';

import {
  BD_SHAREABLE_DETAIL_ROUTES,
  ShareableDetailLink,
} from './shareable-detail-routes';

@Injectable({ providedIn: 'root' })
export class BdShareableSelectService {
  private readonly _router = inject(Router);
  private readonly _detailRoutes = inject(BD_SHAREABLE_DETAIL_ROUTES, {
    optional: true,
  });

  private readonly _selected$ = new Subject<ShareableObj>();
  public readonly selected$: Observable<ShareableObj> =
    this._selected$.asObservable();

  public select(shareable: ShareableObj): void {
    this._selected$.next(shareable);
  }

  public navigate(shareable: ShareableObj): void {
    const link = this._resolveLink(shareable.id, shareable.type);
    if (!link) {
      return;
    }
    this._router.navigate(link.commands, link.extras);
  }

  /**
   * Builds the serialized detail-page URL for a shareable, mirroring the
   * routing rules used by `navigate()`. Returns `undefined` when no detail
   * route is registered for the given type.
   */
  public buildDetailUrl(id: string, type: ShareableType): string | undefined {
    const link = this._resolveLink(id, type);
    if (!link) {
      return undefined;
    }
    return this._router.serializeUrl(
      this._router.createUrlTree(link.commands, link.extras),
    );
  }

  /** Opens the shareable's detail page in a new browser tab. */
  public openInNewTab(id: string, type: ShareableType): void {
    const url = this.buildDetailUrl(id, type);
    if (!url || typeof window === 'undefined') {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private _resolveLink(
    id: string,
    type: ShareableType,
  ): ShareableDetailLink | undefined {
    // The registry is a Map so the factory is fetched via .get() (a static
    // method call) rather than dynamic object-key access — the latter trips
    // Codacy's "retrieve and run functions from the object" rule.
    const buildLink = this._detailRoutes?.get(type);
    return buildLink ? buildLink(id) : undefined;
  }
}
